/**
 * 응답 키벨루 배열을 그리드 rows 형태로 변환하는 함수
 * data :: [{a: 1, b:2}]
 * columns :: [{field: 'a'}, {field: 'b'}]
 * return :: [[1,2]]
 * @param data
 * @param columns
 * @returns {*}
 */
import { computed } from 'vue';
import dayjs from 'dayjs';
import { isNil } from 'lodash-es';
import BigNumber from 'bignumber.js';
import {
  isDateTime,
  numberWithComma,
  replaceInstanceObjectType,
  utcZeroTimeToStandardTime,
} from '@/common/utils/commonUtils';
import {
  ConvertValueToUnits,
  convertValueWithUnit,
  formatValueWithUnit,
} from '@/common/utils/convertUnits.utils';
import type {
  CustomColumn,
  CustomColumnsReadonly,
  GridColumn,
  GridColumnType,
} from '@/common/utils/types';
import { PA_TOGGLE_DATA_TYPE } from '@/common/define/common.define';
import { getOperatorsByType } from '@/common/components/molecules/filterSearch/filterSearch.utils';
import { useEvGridFilterSearch } from '@/common/components/molecules/filterSearch/filterSearch.uses';
import { FilterItem } from '@/common/components/molecules/filterSearch/filterSearch.type';
import {
  UTC_TIME_FORMAT_MILLISECOND,
  UTC_TIME_FORMAT,
} from '@/common/components/molecules/timeController/constants';

export const calculateRowDepth = <T extends Record<string, any>>(row: T): number =>
  row.children?.length ? 1 + Math.max(...row.children.map(calculateRowDepth)) : 1;

export const convertPlanDataToPlanRow = (planData: string) =>
  planData
    ? planData
        .replace(/ /g, '\u00a0')
        .split('/n')
        .map((currentValue, idx) => [idx + 1, currentValue])
    : [];

export function makeRows<
  T extends Record<string, any>,
  Column extends Readonly<CustomColumn> | CustomColumn,
>(
  data: T[] = [],
  columns: readonly Column[],
  columnFormatter?: { [K in Column['field']]?: (field: T[K & keyof T], row?: T) => any },
  originTimeFields?: string[],
  useMilliseconds: boolean = false,
): any[] {
  return data.map((obj, idx) =>
    columns.map((col) => {
      if (columnFormatter?.[col.field]) {
        return columnFormatter[col.field](obj[col.field], obj);
      }

      if (isDateTime(obj[col.field])) {
        if (col.attrs?.useOriginNotFormattedTime) {
          return obj[col.field];
        }
        const useUtc0Time = originTimeFields?.includes(col.field) || col.attrs?.useOriginTime;
        const timeFormat = useMilliseconds ? UTC_TIME_FORMAT_MILLISECOND : UTC_TIME_FORMAT;
        const utc0Time = obj[col.field] ? dayjs(obj[col.field]).format(timeFormat) : null;
        return useUtc0Time ? utc0Time : utcZeroTimeToStandardTime(obj[col.field], timeFormat);
      }

      if (
        typeof obj[col.field] === 'string' &&
        (obj[col.field] === 'i' || obj[col.field] === 'r')
      ) {
        return replaceInstanceObjectType(obj[col.field]);
      }
      if (['rank', 'no'].includes(col.field)) {
        return idx + 1;
      }
      if (
        (col.type === 'string' || col.type === 'stringNumber') &&
        obj[col.field] instanceof BigNumber
      ) {
        return obj[col.field].toString();
      }
      return obj[col.field] ?? null;
    }),
  );
}

/**
 * 그리드 데이터와 그리드 컬럼을 가져와 K-V 매핑된 객체를 리턴해준다.
 * @param data 그리드 데이터
 * @param columns 그리드 컬럼
 */
export const makeRowToObj = <T extends any[], Column extends Readonly<CustomColumn> | CustomColumn>(
  data: T,
  columns: readonly Column[],
  formatter: { [K in Column['field']]?: (field: T[K & keyof T]) => any } = {},
): { [K in Column['field']]?: any } => {
  if (!columns.length) {
    return {};
  }

  return columns.reduce((acc, cur, idx) => {
    if (!acc[cur.field]) {
      acc[cur.field] = null;
    }
    if (formatter[cur.field]) {
      acc[cur.field] = formatter[cur.field](data[idx]);
    } else {
      acc[cur.field] = data[idx];
    }
    return acc;
  }, {});
};

export function findGridColumnIndex<T extends string>({
  fields = [],
  columns = [],
}: {
  fields: T[];
  columns: CustomColumn<T>[] | Readonly<CustomColumn<T>[]>;
}): Record<T, number> {
  const idxMap: Record<T, number> = {} as Record<T, number>;

  const targetFields = new Set(fields);

  for (let idx = 0; idx < columns.length; idx++) {
    const { field } = columns[idx];
    if (targetFields.has(field)) {
      idxMap[field] = idx;
    }
  }

  return idxMap;
}

export function getObjectKeyIndexMap<T extends Readonly<Record<string, any>>>({
  columns = [],
  keyField,
}: {
  columns: Readonly<T[]>;
  keyField: string;
}): Readonly<Record<T[keyof T], number>> {
  const idxMap = {} as Record<T[keyof T], number>;
  for (let idx = 0; idx < columns.length; idx++) {
    const key = columns[idx][keyField];
    idxMap[key] ??= idx;
  }
  return idxMap;
}

export function findCustomColumnIndexMap<T extends string>({
  columns = [],
}: {
  columns: CustomColumn<T>[] | Readonly<CustomColumn<T>[]>;
}): Readonly<Record<T, number>> {
  return getObjectKeyIndexMap<CustomColumn<T>>({
    columns,
    keyField: 'field',
  });
}

export const getRowByFieldInTreeGrid = <T extends { children?: T[] }>(
  treeNodes: T[],
  field: string,
  value: any,
): T | null => {
  for (let ix = 0; ix < treeNodes.length; ix++) {
    const node = treeNodes[ix];
    if (node[field] === value) {
      return node;
    }

    if (node?.children?.length) {
      const result = getRowByFieldInTreeGrid(node.children, field, value);
      if (result) {
        return result;
      }
    }
  }

  return null;
};

export const getFormattedValueWithBestUnit = (
  value: number | null | undefined,
  unit = '',
): string | null => {
  if (isNil(value)) return '';
  const convertValueInfo = convertValueWithUnit({ value, unit });
  return formatValueWithUnit({ originValue: value, ...convertValueInfo });
};

export const getFormattedValueWithComma = (
  value: number | null | undefined,
  unit: string,
  options?: { toUnit?: ConvertValueToUnits },
): string => {
  if (isNil(value)) return '';
  return numberWithComma(convertValueWithUnit({ value, unit }, options).value);
};

export const findColumnProperties = <k extends keyof CustomColumn>(
  findRendererType: CustomColumn['rendererType'],
  findFieldName: k,
  gridColumns: CustomColumn[],
) =>
  gridColumns
    .filter((column) => column?.rendererType === findRendererType)
    .map((column) => column[findFieldName]);

const getSumSuffix = (use: boolean) => (use ? 'Sum' : '');
export const getGridColumnsByToggle = (
  initialGridColumns: CustomColumn[] | CustomColumnsReadonly,
  option?: {
    useSumSuffix?: boolean;
  },
) => {
  return (type: string, gridColumns: CustomColumn[] | CustomColumnsReadonly): GridColumn[] => {
    let columns = gridColumns;
    if (!columns?.length) {
      columns = initialGridColumns;
    }
    const suffix =
      type === PA_TOGGLE_DATA_TYPE.AVG ? 'Avg' : getSumSuffix(option?.useSumSuffix ?? false);
    return columns.map((column: CustomColumn, idx: number) => {
      if (column.attrs?.toggle) {
        return {
          ...column,
          field: `${initialGridColumns[idx].field}${suffix}`,
        };
      }
      return column;
    });
  };
};

export const useFilterSearch = (columns: GridColumn[]) => {
  const filterSearchItems: FilterItem[] = columns
    .filter(({ searchable }) => searchable)
    .map(({ caption, field, type, defaultSearchField }) => {
      const values: {
        multi: boolean;
        items: { id: string; name: string }[];
      } = {
        multi: type === 'boolean',
        items:
          type === 'boolean'
            ? [
                { id: 'true', name: 'True' },
                { id: 'false', name: 'False' },
              ]
            : [],
      };

      return {
        key: {
          id: field,
          name: caption,
        },
        operators: getOperatorsByType(type as GridColumnType),
        values,
        defaultSearchField,
      };
    });

  const { filterSearchResultMV, filterGridByFilterSearch, changeStateObjectRowsByFilterSearch } =
    useEvGridFilterSearch({
      columns: computed(() => columns),
      filterItems: filterSearchItems,
    });

  return {
    filterSearchItems,
    filterSearchResultMV,
    filterGridByFilterSearch,
    changeStateObjectRowsByFilterSearch,
  };
};

export const processTreeNodes = <T extends Record<string, any>>({
  row,
  maxDepth,
  option,
}: {
  row: T;
  maxDepth: number;
  option: Record<string, any> & {
    handler: (param: { row: T; depth: number; children: T[]; option: Record<string, any> }) => void;
  };
}) => {
  const queue = [{ row, depth: 1 }];

  while (queue.length > 0) {
    const { row: currentRow, depth } = queue.shift()!;

    if (depth <= maxDepth) {
      if (option.handler) {
        option.handler({ row: currentRow, depth, children: currentRow?.children, option });
      }

      if (currentRow?.children && Array.isArray(currentRow?.children)) {
        currentRow?.children.forEach((child) => {
          queue.push({ row: child, depth: depth + 1 });
        });
      }
    }
  }
};

export const getColumnsWithoutUnitCaption = (columns: CustomColumn[]) => {
  return columns.map((col) => {
    if (col.rendererType === 'sec') {
      return {
        ...col,
        caption: col.caption.replace(/\(sec\)/gi, '').trim(),
      };
    }
    return col;
  });
};

export const stopContextMenu = (event: MouseEvent) => {
  event.preventDefault();
  event.stopImmediatePropagation();
  event.stopPropagation();
};
